import { useEffect } from "react";
import { useAuthStore } from "../store/authStore";
import { api } from "../api/client";

// Mantiene viva la sesión mientras el usuario tenga la pestaña abierta:
//  - Cada 25 min hace ping a /api/auth/me
//  - Si la respuesta es 401 (token expirado), el interceptor de client.js
//    redirige a /login con flag "sesión expirada"
//  - Si todo OK, no hace nada — solo confirma que el token sigue válido
//
// No es un refresh real (el JWT no se renueva), pero permite detectar
// expiración antes de que el usuario haga una acción y se le caiga el form.
// Para refresh real habría que añadir un endpoint /api/auth/refresh y un
// refresh token persistente — pospuesto para una siguiente sesión.
//
// Decisiones:
//  - 25 min (no 5 min) para no inflar requests al worker.
//  - Solo si hay token (no corremos en pantalla de login).
//  - Pausa cuando la pestaña no está visible (visibilitychange) para
//    no gastar requests inútiles si el usuario tiene 5 pestañas abiertas.

const KEEPALIVE_INTERVAL_MS = 25 * 60 * 1000;

export function useSessionKeepalive() {
  const token = useAuthStore((s) => s.token);

  useEffect(() => {
    if (!token) return;

    let timer = null;

    const ping = async () => {
      try { await api.get("/api/auth/me"); } catch { /* interceptor lo maneja */ }
    };

    const start = () => {
      if (timer) return;
      timer = setInterval(ping, KEEPALIVE_INTERVAL_MS);
    };
    const stop = () => {
      if (timer) { clearInterval(timer); timer = null; }
    };

    // Iniciar inmediato si la pestaña está visible
    if (document.visibilityState === "visible") start();

    const onVis = () => {
      if (document.visibilityState === "visible") {
        ping();  // verifica al volver a la pestaña
        start();
      } else {
        stop();
      }
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [token]);
}
